import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { StyledModal } from "components/shared";
import { Copy } from "components/components/atoms";
import useBreakpoint from "utils/hooks/useBreakpoint";
import CloseIcon from "components/icons/Close";
import NumberFormat from "react-number-format";
import cx from "classnames";

const StyledHeading = styled.h1`
  margin-bottom: 0;
`;

const ModalContent = styled.div`
  ${({ isMobile }) =>
    isMobile
      ? `
        width: 91.5vw;
        height: 90vh;
        margin: auto;
      `
      : `
        width: 70vw;
        height: 80vh;
        margin: auto;
        max-width: 50rem;
      `}
`;
const StyledSpan = styled.span`
  flex: 1;
`;

const calculateMortgage = (totalAmount, downpayment, rate, termYears) => {
  const amount = totalAmount - (totalAmount * downpayment) / 100;
  const adjustedRate = rate / 100 / 12;
  const termMonths = termYears * 12;
  if (adjustedRate === 0) return amount / termMonths;
  const pvif = Math.pow(1 + adjustedRate, termMonths);
  const pmt = (adjustedRate / (pvif - 1)) * (amount * pvif);
  return Math.round(pmt);
};

const MortgageCalculatorModal = (props) => {
  const {
    isOpen = false,
    onClose = () => {},
    amountValue = 0,
    title = "",
    ctaText = "",
    ctaTitle = "",
    ctaURL = "",
    disclaimer = "",
    downpaymentInit = 0,
    interestRateInit = 0,
    loanTermInit = 10,
  } = props;

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const closeIframeModalRef = useRef();
  const [isEndOfModalIframe, setIsEndOfModalIframe] = useState(false);
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const [downpaymentValue, setDownpaymentValue] = useState(
    downpaymentInit > 50 ? 50 : downpaymentInit
  );
  const [interestRatevalue, setInterestRateValue] = useState(
    interestRateInit > 10 ? 10 : interestRateInit
  );
  const [loanTermValue, setLoanTermValue] = useState(
    loanTermInit < 10 ? 10 : loanTermInit
  );
  const result = calculateMortgage(
    amountValue,
    downpaymentValue,
    interestRatevalue,
    loanTermValue
  );
  useEffect(() => {
    setTimeout(() => {
      if (closeIframeModalRef.current) {
        const element = closeIframeModalRef.current;
        if (element) {
          element.focus();
        }
      }
    }, 0);
  }, [isOpen]);

  useEffect(() => {
    const element = closeIframeModalRef.current;
    if (isEndOfModalIframe && element) {
      element.focus();
    }
  }, [isEndOfModalIframe]);

  return (
    <StyledModal
      isOpen={isOpen}
      className="bg-black-faded-light select-none flex w-full"
      data-aos="fade"
      tabIndex={0}
      id="MortgageCalculatorModal"
    >
      <ModalContent
        isMobile={isMobile}
        className="overflow-auto h-full rounded"
        aria-modal="true"
        role="dialog"
        aria-labelledby="mortgageCaculatorModalLabel"
      >
        <div className="w-full flex flex-col bg-white rounded text-action-blue pt-10 lg:pt-20 px-12 md:px-16">
          <button
            className="relative flex text-right -mt-8 -mr-10 md:-mr-12 md:-mt-16 text-dark-gray-bg justify-end"
            aria-label={"Close MortgageCalculatorModal"}
            aria-describedby={"Close MortgageCalculatorModal button"}
            onClick={() => {
              typeof onClose === "function" && onClose();
              setDownpaymentValue(downpaymentInit > 50 ? 50 : downpaymentInit);
              setInterestRateValue(
                interestRateInit > 10 ? 10 : interestRateInit
              );
              setLoanTermValue(loanTermInit < 10 ? 10 : loanTermInit);
            }}
            ref={closeIframeModalRef}
            onFocus={(e) => {
              setIsEndOfModalIframe(false);
            }}
          >
            <CloseIcon className="w-8" />
          </button>
          <StyledHeading
            className="font-bold leading-tiny font-trade-gothic-20 mb-6 md:mb-10-mattamy text-4xl text-mattamy-blue pb-5 md:pb-6 pt-6 md:pt-0 tracking-tight text-center"
            id="mortgageCaculatorModalLabel"
          >
            {title}
          </StyledHeading>
          <label
            htmlFor="interest-range"
            className="block mb-2 text-sm font-medium text-mattamy-blue font-graphie"
          >
            {"Interest Rate (" + interestRatevalue + "%)"}
          </label>
          <input
            id="interest-range"
            type="range"
            min="0"
            max="10"
            step="0.25"
            list="interestTicks"
            value={interestRatevalue}
            className="mb-2 w-full h-2 bg-gray-200 rounded-lg  cursor-pointer dark:bg-gray-700"
            onChange={(e) => {
              const target = e.target;
              if (target) {
                const value = target.value;
                setInterestRateValue(value);
              }
            }}
          />
          <datalist id="interestTicks">
            <option value="0">0</option>
            <option value="2">2</option>
            <option value="4">4</option>
            <option value="6">6</option>
            <option value="8">8</option>
            <option value="10">10</option>
          </datalist>
          <div className="w-full flex text-mattamy-blue mb-4 ">
            <StyledSpan className="font-graphie text-left">0</StyledSpan>
            <StyledSpan className="font-graphie text-left ml-8">2</StyledSpan>
            <StyledSpan className="font-graphie text-center mr-4">4</StyledSpan>
            <StyledSpan className="font-graphie text-center ml-3">6</StyledSpan>
            <StyledSpan className="font-graphie text-right mr-8">8</StyledSpan>
            <StyledSpan className="font-graphie text-right">10</StyledSpan>
          </div>

          <label
            htmlFor="loan-range"
            className="block mb-2 text-sm font-medium text-mattamy-blue font-graphie"
          >
            {"Loan Term (" + loanTermValue + " Years)"}
          </label>
          <input
            id="loan-range"
            type="range"
            min="10"
            max="30"
            value={loanTermValue}
            list="loanTicks"
            className="mb-2 w-full h-2 bg-gray-200 rounded-lg  cursor-pointer dark:bg-gray-700"
            onChange={(e) => {
              const target = e.target;
              if (target) {
                const value = target.value;
                setLoanTermValue(value);
              }
            }}
          />
          <datalist id="loanTicks">
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="25">25</option>
            <option value="30">30</option>
          </datalist>
          <div className="w-full flex text-mattamy-blue mb-4 ">
            <StyledSpan className="font-graphie text-left">10</StyledSpan>
            <StyledSpan className="font-graphie text-left ml-8">15</StyledSpan>
            <StyledSpan className="font-graphie text-center">20</StyledSpan>
            <StyledSpan className="font-graphie text-right mr-8">25</StyledSpan>
            <StyledSpan className="font-graphie text-right">30</StyledSpan>
          </div>

          <label
            htmlFor="percentage-range"
            className="block mb-2 text-sm font-medium text-mattamy-blue font-graphie"
          >
            {"Percentage of Down Payment (" + downpaymentValue + "%)"}
          </label>
          <input
            id="percentage-range"
            type="range"
            min="0"
            max="50"
            value={downpaymentValue}
            list="percentajeTicks"
            className="mb-2 w-full h-2 bg-gray-200 rounded-lg  cursor-pointer dark:bg-gray-700"
            onChange={(e) => {
              const target = e.target;
              if (target) {
                const value = target.value;
                setDownpaymentValue(value);
              }
            }}
          />
          <datalist id="percentajeTicks">
            <option value="0"></option>
            <option value="10"></option>
            <option value="20"></option>
            <option value="30"></option>
            <option value="40"></option>
            <option value="50"></option>
          </datalist>
          <div className="w-full flex text-mattamy-blue mb-4 ">
            <StyledSpan className="font-graphie text-left">0</StyledSpan>
            <StyledSpan className="font-graphie text-left ml-4">10</StyledSpan>
            <StyledSpan className="font-graphie text-center mr-2">
              20
            </StyledSpan>
            <StyledSpan className="font-graphie text-center ml-2">
              30
            </StyledSpan>
            <StyledSpan className="font-graphie text-right mr-4">40</StyledSpan>
            <StyledSpan className="font-graphie text-right">50</StyledSpan>
          </div>

          {!isNaN(result) && result > 0 && (
            <>
              <div className="flex mb-1">
                <Copy className="text-left text-sm mt-1 tracking-tight mr-4 ">
                  {"Estimated Monthly Payment (principal and interest only): "}
                </Copy>
              </div>
              <div className="flex mb-4">
                <StyledHeading className="font-bold leading-tiny font-trade-gothic-20 mb-6 md:mb-10-mattamy text-4xl text-mattamy-blue text-left text-sm t-1  tracking-tight">
                  <NumberFormat
                    value={result}
                    className="break-all leading-none"
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"$"}
                    decimalScale={2}
                  />
                </StyledHeading>
              </div>
            </>
          )}

          {ctaTitle && (
            <div
              className={
                "flex flex-col md:flex-row items-center justify-between w-full rounded-lg bg-mattamy-blue py-2 px-1 mt-4"
              }
            >
              <div
                className={cx(
                  "w-full  flex md:flex-row items-left justify-center md:justify-start",
                  {
                    "lg:w-5/12 xl:w-7/12": ctaText && ctaURL,
                  }
                )}
              >
                <StyledHeading
                  className="text-3xl tracking-wide font-bold font-trade-gothic-20
                  text-center md:text-left mt-5 pb-4 mx-4 leading-tight text-white"
                >
                  {ctaTitle}
                </StyledHeading>
              </div>
              {ctaText && ctaURL && (
                <div
                  className="w-full print:hidden lg:w-7/12 xl:w-5/12 flex flex-col md:flex-row items-center
                justify-center md:justify-end md:text-right"
                >
                  <button
                    className={
                      "uppercase font-trade-gothic-20 rounded pb-3 pt-4 px-8 mx-8 md:mx-4 tracking-wide w-full md:w-auto text-white border-1-5 bg-transparent border-white hover:text-mattamy-blue hover:bg-white"
                    }
                    onClick={() => {
                      window.location.href = ctaURL;
                    }}
                    buttonDescription={"Get Started. "}
                  >
                    {ctaText}
                  </button>
                </div>
              )}
            </div>
          )}

          <Copy className="text-left text-xs t-1 py-5 pb-24 lg:pb-10 tracking-tight">
            {disclaimer}
          </Copy>

          <div
            tabIndex={0}
            onFocus={(e) => {
              setIsEndOfModalIframe(true);
            }}
          />
        </div>
      </ModalContent>
    </StyledModal>
  );
};

export default MortgageCalculatorModal;
